export const SITE_NAME = 'Bedroom Projects';

/** Production site origin (canonical for SEO and social previews). */
export const SITE_ORIGIN = 'https://bedroomduo.co.uk';

export const SITE_TAGLINE = 'London film & television studio';

export const DEFAULT_TITLE = `${SITE_NAME} — ${SITE_TAGLINE}`;

export const DEFAULT_DESCRIPTION =
  'Bedroom Projects is a London film and television studio. Soren Harrison and Amir Hossain create music videos, campaigns, and moving image for artists and brands including Arlo Parks, beabadoobee, The 1975, and Dr. Martens.';

export const DEFAULT_KEYWORDS = [
  'Bedroom Projects',
  'film studio',
  'television production',
  'music video production',
  'London film studio',
  'creative studio',
  'moving image',
  'directors',
  'Soren Harrison',
  'Amir Hossain'
].join(', ');

export const THEME_COLOR = '#feeddc';

export const DEFAULT_OG_IMAGE_PATH = '/web-app-manifest-512x512.png';

export const SAME_AS = [
  'https://vimeo.com/bedroomprojects',
  'https://www.instagram.com/bedroom.duo/'
];

export const CONTACT_EMAILS = ['amir@blacksocks.studio', 'sorenharrison100@gmail.com'];

export const REPRESENTATION_CONTACTS = [
  { region: 'UK', agency: 'FRIEND', email: 'info@friendlondon.tv' },
  { region: 'CANADA', agency: 'MERCHANT', email: 'hello@merchant.ws' }
];

export const DEFAULT_OG_IMAGE_WIDTH = 960;
export const DEFAULT_OG_IMAGE_HEIGHT = 540;

function createMuxStreamSourceFromPublicVideoIdentifier(publicVideoIdentifier) {
  return `https://stream.mux.com/${encodeURIComponent(publicVideoIdentifier)}.m3u8`;
}

function createMuxPosterImageUrlFromPublicVideoIdentifier(publicVideoIdentifier) {
  const trimmed = (publicVideoIdentifier || '').trim();
  if (!trimmed) return '';
  const query = new URLSearchParams({
    time: '0',
    width: String(DEFAULT_OG_IMAGE_WIDTH),
    fit_mode: 'preserve'
  });
  return `https://image.mux.com/${encodeURIComponent(trimmed)}/thumbnail.jpg?${query.toString()}`;
}

export function formatDurationIso8601(durationSeconds) {
  const totalSeconds = Math.round(Number(durationSeconds));
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `PT${hours}H${minutes}M${seconds}S`;
  if (minutes > 0) return `PT${minutes}M${seconds}S`;
  return `PT${seconds}S`;
}

export function resolveChannelPosterUrl(channel) {
  const muxId = (channel?.muxId || channel?.muxPublicVideoIdentifier || '').trim();
  return muxId ? createMuxPosterImageUrlFromPublicVideoIdentifier(muxId) : '';
}

export function resolveChannelContentUrl(channel) {
  const muxId = (channel?.muxId || channel?.muxPublicVideoIdentifier || '').trim();
  return muxId ? createMuxStreamSourceFromPublicVideoIdentifier(muxId) : '';
}

export function resolveChannelOgImageDimensions(channel) {
  const width = Number(channel?.posterWidth);
  const height = Number(channel?.posterHeight);
  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    return { width, height };
  }
  return { width: DEFAULT_OG_IMAGE_WIDTH, height: DEFAULT_OG_IMAGE_HEIGHT };
}

function ensureTrailingSlash(url) {
  return url.endsWith('/') ? url : `${url}/`;
}

/** Base URL for SEO: live origin on production, SITE_ORIGIN on local dev. */
export function getSiteBaseUrl() {
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    const isLocal =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.local');
    if (!isLocal) {
      const host = port ? `${hostname}:${port}` : hostname;
      return ensureTrailingSlash(`${protocol}//${host}`);
    }
  }
  return ensureTrailingSlash(SITE_ORIGIN);
}

export function resolveAbsoluteSiteUrl(path = '/') {
  const base = getSiteBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  try {
    return new URL(normalizedPath, base).toString();
  } catch {
    return base;
  }
}

export function resolveAbsoluteAssetUrl(assetPath = DEFAULT_OG_IMAGE_PATH) {
  return resolveAbsoluteSiteUrl(assetPath);
}

export function buildPageTitle(channelTitle) {
  const trimmed = (channelTitle || '').trim();
  return trimmed ? `${trimmed} | ${SITE_NAME}` : DEFAULT_TITLE;
}

export function getChannelCategoryLabelForSeo(channel) {
  const raw = (channel?.categoryTitle || '').trim();
  if (!raw) return '';
  if (raw.toLowerCase() === 'commercials') return 'Commercial';
  return raw;
}

function formatCategoryPhraseForDescription(categoryLabel) {
  const normalized = String(categoryLabel || '').trim().toLowerCase();
  if (normalized === 'commercial') return 'a commercial';
  if (normalized === 'music videos') return 'a music video';
  if (normalized === 'fiction') return 'a fiction film';
  if (!normalized) return 'a film';
  return `a ${normalized} film`;
}

export function buildChannelDescription(channel) {
  const campaignDescription = (channel?.description || '').trim();
  const title = (channel?.title || channel?.slug || '').trim();
  const categoryLabel = getChannelCategoryLabelForSeo(channel);
  const categoryPhrase = formatCategoryPhraseForDescription(categoryLabel);
  const studioPhrase = `${SITE_NAME}, a London film and television studio`;

  if (title && categoryLabel && campaignDescription) {
    return `${title} — ${categoryPhrase} by ${studioPhrase}. ${campaignDescription}`;
  }
  if (title && categoryLabel) {
    return `${title} — ${categoryPhrase} by ${studioPhrase}.`;
  }
  if (title && campaignDescription) {
    return `${title} — ${campaignDescription}. Watch on ${studioPhrase}.`;
  }
  if (title) {
    return `Watch ${title} on ${studioPhrase}.`;
  }
  return DEFAULT_DESCRIPTION;
}

export function buildChannelKeywords(channel) {
  const categoryLabel = getChannelCategoryLabelForSeo(channel);
  if (!categoryLabel) return DEFAULT_KEYWORDS;
  const parts = [DEFAULT_KEYWORDS, categoryLabel, `${categoryLabel} production`];
  return Array.from(new Set(parts.join(', ').split(',').map(item => item.trim()).filter(Boolean))).join(', ');
}

export function buildOrganizationJsonLd(absoluteBaseUrl) {
  const base = (absoluteBaseUrl || '').replace(/\/?$/, '/');
  const logo = base ? new URL(DEFAULT_OG_IMAGE_PATH, base).toString() : DEFAULT_OG_IMAGE_PATH;

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    url: base || undefined,
    logo,
    sameAs: SAME_AS,
    email: CONTACT_EMAILS,
    knowsAbout: [
      'Film production',
      'Television production',
      'Music video production',
      'Commercial production',
      'Art direction'
    ]
  };
}

export function buildWebSiteJsonLd(absoluteBaseUrl) {
  const base = (absoluteBaseUrl || '').replace(/\/?$/, '/');
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    url: base || undefined,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME
    }
  };
}

export function buildVideoObjectJsonLd({ channel, canonicalUrl, absoluteBaseUrl } = {}) {
  if (!channel) return null;

  const base = (absoluteBaseUrl || '').replace(/\/?$/, '/');
  const thumbnailUrl = resolveChannelPosterUrl(channel);
  const name = (channel.title || channel.slug || '').trim();
  const description = buildChannelDescription(channel);

  const videoObject = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: name || SITE_NAME,
    description,
    embedUrl: canonicalUrl || undefined,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: base ? new URL(DEFAULT_OG_IMAGE_PATH, `${base}/`).toString() : DEFAULT_OG_IMAGE_PATH
    }
  };

  if (thumbnailUrl) videoObject.thumbnailUrl = thumbnailUrl;
  const contentUrl = resolveChannelContentUrl(channel);
  if (contentUrl) videoObject.contentUrl = contentUrl;
  if (channel.uploadDate) videoObject.uploadDate = channel.uploadDate;
  if (channel.durationSeconds) {
    const duration = formatDurationIso8601(channel.durationSeconds);
    if (duration) videoObject.duration = duration;
  }

  return videoObject;
}

export function buildVideoItemListJsonLd({ channels, absoluteBaseUrl, buildChannelPath }) {
  const base = (absoluteBaseUrl || '').replace(/\/?$/, '/');
  const list = Array.isArray(channels) ? channels : [];

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${SITE_NAME} videos`,
    itemListElement: list.map((channel, index) => {
      const path = buildChannelPath ? `/${buildChannelPath(channel)}` : '/';
      const itemUrl = base ? new URL(path, `${base}/`).toString() : path;
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: (channel.title || channel.slug || '').trim() || `Channel ${index + 1}`,
        url: itemUrl
      };
    })
  };
}
